import { memo, useState, useEffect } from "react";
import { isEmpty, range } from "ramda";
import { Container, Typography } from "@mui/material";
import PropTypes from "prop-types";

import { useUsers, useFaces, useFaceOrg, useCalendar, useLoading } from "hooks";
import { trackCalendarHit } from "tracking/google/analytics";
import UserPresenter from "presenters/UserPresenter";
import Placeholder from "components/Placeholder";
import { ENTRY_POINTS } from "enums";

import DisplayPerson from "./components/DisplayPerson/DisplayPerson";
import AlgoliaCalendar from "./components/AlgoliaCalendar/AlgoliaCalendar";
import { Root, Title, BlankView, StyledButton, People } from "./styles";

const UpcomingDates = ({ isDateTypeDisplay = true, peopleLimit = 6, context }) => {
  const { currentUser } = useUsers();
  const { openModal: openFacesModal, resetAdded } = useFaces();
  const { celebrations, loadCelebrations, peopleParams } = useCalendar();
  const { func: loadPeopleWithLoader, isFinished: isPeopleLoaded } = useLoading(loadCelebrations);
  const [ctaCount, setCtaCount] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalArgs, setModalArgs] = useState({});
  const { setEntryPoint } = useFaceOrg();

  useEffect(() => {
    return () => {
      resetAdded();
    };
  }, []);

  const handleAddBirthdays = () => openFacesModal();
  const handlePersonClick = (args) => {
    trackCalendarHit(args.dateType);
    setShowModal(true);
    setModalArgs({ ...args });
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setModalArgs({});
  };

  const renderComponent = () => {
    if (isEmpty(celebrations)) {
      return (
        <BlankView>
          <Typography variant="h6">Hey, you have no upcoming occasions!</Typography>
          <Typography variant="h6">Add dates to unlock the fun!</Typography>

          <StyledButton
            data-testid="upcomingdates-buttonadd"
            variant="contained"
            color="primary"
            onClick={() => {
              handleAddBirthdays();
              setEntryPoint(ENTRY_POINTS.CALENDAR);
            }}
          >
            Add occasions
          </StyledButton>
        </BlankView>
      );
    }

    return (
      <People>
        {!isEmpty(celebrations) &&
          celebrations.slice(0, peopleLimit).map(({ person, key, dateType, dateToCelebrate }) => {
            return (
              <DisplayPerson
                person={person}
                dateType={dateType}
                dateToCelebrate={dateToCelebrate}
                key={key}
                onClick={handlePersonClick}
                isDateTypeDisplay={isDateTypeDisplay}
              />
            );
          })}
        {ctaCount.map((index) => (
          <DisplayPerson
            isCTA
            key={index}
            onClick={() => {
              handleAddBirthdays();
              setEntryPoint(ENTRY_POINTS.CALENDAR);
            }}
            isDateTypeDisplay={isDateTypeDisplay}
          />
        ))}
      </People>
    );
  };

  useEffect(() => {
    const ctaLength = range(0, 6 - celebrations.length);
    setCtaCount(ctaLength);
  }, [celebrations]);

  useEffect(() => {
    if (UserPresenter.isRegistered(currentUser)) {
      loadPeopleWithLoader(peopleParams(true));
    }
  }, [currentUser]);

  if (UserPresenter.notRegistered(currentUser)) {
    return null;
  }

  return (
    <Root>
      <Container maxWidth="lg">
        <Title variant="h5">Upcoming Occasions</Title>
        {!isPeopleLoaded ? <Placeholder variant="rect" /> : renderComponent()}
        {showModal && <AlgoliaCalendar {...modalArgs} onClose={handleCloseModal} context={context} />}
      </Container>
    </Root>
  );
};

UpcomingDates.propTypes = {
  isDateTypeDisplay: PropTypes.bool,
  peopleLimit: PropTypes.number,
  context: PropTypes.string,
};

export default memo(UpcomingDates);
